import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { FaPlane, FaLaptopCode } from 'react-icons/fa';
import { useLanguage } from '@/contexts/LanguageContext';
import PhotoGallery from '@/components/PhotoGallery';
import HeroSlider from '@/components/HeroSlider';

const Home = () => {
  const { language } = useLanguage();

  // Photos de destinations réelles avec drapeaux — chaque image matche la ville
  const destinationPhotos = [
    { url: 'https://images.unsplash.com/photo-1518684079-3c830dcef090?w=800', alt: 'Dubai - Burj Khalifa', overlay: '🇦🇪 Dubai' },
    { url: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800', alt: 'Paris - Tour Eiffel', overlay: '🇫🇷 Paris' },
    { url: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=800', alt: 'Tokyo - Shibuya', overlay: '🇯🇵 Tokyo' },
    { url: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=800', alt: 'New York - Manhattan', overlay: '🇺🇸 New York' },
    { url: 'https://images.unsplash.com/photo-1529655683826-aba9b3e77383?w=800', alt: 'London - Big Ben', overlay: '🇬🇧 London' },
    { url: 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=800', alt: 'Istanbul - Hagia Sophia', overlay: '🇹🇷 Istanbul' },
    { url: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800', alt: 'Maldives - overwater bungalows', overlay: '🇲🇻 Maldives' },
    { url: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800', alt: 'Bali - rice terraces', overlay: '🇮🇩 Bali' },
    { url: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=800', alt: 'Rome - Colosseum', overlay: '🇮🇹 Rome' },
    { url: 'https://images.unsplash.com/photo-1583422409516-2895a77efded?w=800', alt: 'Barcelona - Sagrada Familia', overlay: '🇪🇸 Barcelona' },
  ];

  // Photos Innovation & Technologie — chaque image matche un volet tech
  const techPhotos = [
    { url: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800', alt: 'Circuit board — IoT & hardware' },
    { url: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800', alt: 'Code on screen — software dev' },
    { url: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800', alt: 'Team developers' },
    { url: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800', alt: 'Data center — cloud' },
    { url: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800', alt: 'AI / neural network' },
    { url: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800', alt: 'Analytics dashboard' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* Hero Slider with multiple images */}
      <HeroSlider language={language} />

      {/* Section "Nos services" - Two columns exact from maquette */}
      <section className="py-20 bg-white" data-testid="services-section">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-maquette-text mb-4">
              {language === 'fr' && 'Nos services'}
              {language === 'en' && 'Our services'}
              {language === 'ar' && 'خدماتنا'}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Agence de voyage personnalisée */}
            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow" data-testid="service-voyage">
              <div className="flex justify-center mb-6">
                <div className="bg-maquette-blue/10 rounded-full p-6">
                  <FaPlane className="text-maquette-blue text-5xl" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-maquette-text mb-4 text-center">
                {language === 'fr' && 'Agence de voyage personnalisée'}
                {language === 'en' && 'Personalized Travel Agency'}
                {language === 'ar' && 'وكالة سفر مخصصة'}
              </h3>
              <p className="text-maquette-gray text-center leading-relaxed mb-6">
                {language === 'fr' && 'Circuits sur mesure, voyages d\'affaires et séjours inoubliables pour particuliers et entreprises. Nos experts créent l\'expérience qui vous ressemble.'}
                {language === 'en' && 'Custom tours, business travel and unforgettable stays for individuals and companies. Our experts create the experience that suits you.'}
                {language === 'ar' && 'جولات مخصصة، سفر أعمال وإقامات لا تُنسى للأفراد والشركات. يخلق خبراؤنا التجربة التي تناسبك.'}
              </p>
              <div className="text-center">
                <Link
                  to="/voyages"
                  className="inline-flex items-center space-x-2 bg-maquette-blue text-white px-6 py-3 rounded-md font-semibold hover:bg-maquette-dark transition-all shadow-sm"
                >
                  <span>
                    {language === 'fr' && 'Découvrir'}
                    {language === 'en' && 'Discover'}
                    {language === 'ar' && 'اكتشف'}
                  </span>
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            {/* Développement et solutions digitales */}
            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow" data-testid="service-it">
              <div className="flex justify-center mb-6">
                <div className="bg-maquette-blue/10 rounded-full p-6">
                  <FaLaptopCode className="text-maquette-blue text-5xl" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-maquette-text mb-4 text-center">
                {language === 'fr' && 'Développement et solutions digitales'}
                {language === 'en' && 'Development and Digital Solutions'}
                {language === 'ar' && 'التطوير والحلول الرقمية'}
              </h3>
              <p className="text-maquette-gray text-center leading-relaxed mb-6">
                {language === 'fr' && 'Applications web et mobile, cloud computing et transformation digitale pour propulser votre entreprise vers l\'avenir. Innovation et performance garanties.'}
                {language === 'en' && 'Web and mobile applications, cloud computing and digital transformation to propel your business into the future. Innovation and performance guaranteed.'}
                {language === 'ar' && 'تطبيقات الويب والهاتف المحمول، الحوسبة السحابية والتحول الرقمي لدفع عملك نحو المستقبل. الابتكار والأداء مضمونان.'}
              </p>
              <div className="text-center">
                <Link
                  to="/it-services"
                  className="inline-flex items-center space-x-2 bg-maquette-blue text-white px-6 py-3 rounded-md font-semibold hover:bg-maquette-dark transition-all shadow-sm"
                >
                  <span>
                    {language === 'fr' && 'Découvrir'}
                    {language === 'en' && 'Discover'}
                    {language === 'ar' && 'اكتشف'}
                  </span>
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Destinations de voyage - Gallery */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-maquette-text mb-4">
              {language === 'fr' && 'Nos destinations de voyage'}
              {language === 'en' && 'Our travel destinations'}
              {language === 'ar' && 'وجهات سفرنا'}
            </h2>
            <p className="text-xl text-maquette-gray">
              {language === 'fr' && 'Découvrez les destinations les plus prisées dans le monde'}
              {language === 'en' && 'Discover the most popular destinations in the world'}
              {language === 'ar' && 'اكتشف الوجهات الأكثر شعبية في العالم'}
            </p>
          </div>
          <PhotoGallery photos={destinationPhotos} slidesToShow={4} />
        </div>
      </section>

      {/* Section Innovation & Technologie - Gallery */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-maquette-text mb-4">
              {language === 'fr' && 'Innovation & Technologie'}
              {language === 'en' && 'Innovation & Technology'}
              {language === 'ar' && 'الابتكار والتكنولوجيا'}
            </h2>
            <p className="text-xl text-maquette-gray">
              {language === 'fr' && 'Solutions technologiques modernes pour votre entreprise'}
              {language === 'en' && 'Modern technology solutions for your business'}
              {language === 'ar' && 'حلول تكنولوجية حديثة لعملك'}
            </p>
          </div>
          <PhotoGallery photos={techPhotos} slidesToShow={3} />
        </div>
      </section>

      {/* Section "À propos de nous" + "Contact rapide" - Two columns exact from maquette */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* À propos de nous */}
            <div>
              <h2 className="text-3xl font-bold text-maquette-text mb-6">
                {language === 'fr' && 'À propos de nous'}
                {language === 'en' && 'About us'}
                {language === 'ar' && 'من نحن'}
              </h2>
              <p className="text-maquette-gray leading-relaxed mb-4">
                {language === 'fr' && 'HORIZONS CONNECTÉS est une entreprise basée à Muscat, Oman, spécialisée dans deux domaines complémentaires : le tourisme et les technologies de l\'information.'}
                {language === 'en' && 'HORIZONS CONNECTÉS is a company based in Muscat, Oman, specialized in two complementary areas: tourism and information technology.'}
                {language === 'ar' && 'HORIZONS CONNECTÉS هي شركة مقرها مسقط، عمان، متخصصة في مجالين متكاملين: السياحة وتكنولوجيا المعلومات.'}
              </p>
              <p className="text-maquette-gray leading-relaxed mb-4">
                {language === 'fr' && 'Notre mission est de créer des expériences exceptionnelles, que ce soit à travers des voyages mémorables ou des solutions digitales innovantes qui transforment votre entreprise.'}
                {language === 'en' && 'Our mission is to create exceptional experiences, whether through memorable travels or innovative digital solutions that transform your business.'}
                {language === 'ar' && 'مهمتنا هي خلق تجارب استثنائية، سواء من خلال رحلات لا تُنسى أو حلول رقمية مبتكرة تحول عملك.'}
              </p>
              <Link
                to="/about"
                className="inline-flex items-center space-x-2 text-maquette-blue font-semibold hover:text-maquette-dark transition-colors"
              >
                <span>
                  {language === 'fr' && 'En savoir plus'}
                  {language === 'en' && 'Learn more'}
                  {language === 'ar' && 'اعرف المزيد'}
                </span>
                <ArrowRight size={18} />
              </Link>
            </div>

            {/* Contact rapide - Form */}
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-maquette-text mb-6">
                {language === 'fr' && 'Contact rapide'}
                {language === 'en' && 'Quick contact'}
                {language === 'ar' && 'اتصال سريع'}
              </h2>
              <form className="space-y-4" data-testid="quick-contact-form">
                <div>
                  <input
                    type="text"
                    placeholder={language === 'fr' ? 'Nom' : language === 'en' ? 'Name' : 'الاسم'}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-maquette-blue"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    placeholder={language === 'fr' ? 'E-mail' : language === 'en' ? 'E-mail' : 'البريد الإلكتروني'}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-maquette-blue"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    placeholder={language === 'fr' ? 'Sujet' : language === 'en' ? 'Subject' : 'الموضوع'}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-maquette-blue"
                    data-testid="input-subject"
                  />
                </div>
                <div>
                  <textarea
                    rows="4"
                    placeholder={language === 'fr' ? 'Message' : language === 'en' ? 'Message' : 'الرسالة'}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-maquette-blue resize-none"
                    data-testid="input-message"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-maquette-dark text-white px-6 py-3 rounded-md font-semibold hover:bg-maquette-blue transition-colors shadow-sm"
                  data-testid="submit-contact"
                >
                  {language === 'fr' && 'Envoyer'}
                  {language === 'en' && 'Send'}
                  {language === 'ar' && 'إرسال'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
